import { useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';
import IntercomMapping from 'features/device/domain/models/intercom-mapping';
import { useValidateIntercomMappingButtonId } from 'features/device/domain/queries/device-queries';
import { ReadIntercomIpRoomUnitsSystemResponse } from 'features/systems/ip-unit/domain/models/read-intercom-ip-room-units-response';

type IntercomMappingsForm = {
  intercomMappings: IntercomMapping[];
};

type UseIntercomMappings = {
  intercomMappings: IntercomMapping[];
  deletedIntercomMappings: IntercomMapping[];
  addIntercomMapping: () => void;
  deleteIntercomMapping: (index: number) => void;
};

export type ButtonIdValidationResult = true | 'notUnique';

export const useIntercomMappings = (): UseIntercomMappings => {
  const form = useFormContext<IntercomMappingsForm>();
  const [deletedIntercomMappings, setDeletedIntercomMappings] = useState<IntercomMapping[]>([]);
  const { append, replace } = useFieldArray({
    control: form.control,
    name: 'intercomMappings',
    keyName: 'fieldId',
  });
  const intercomMappings = useWatch({ control: form.control, name: 'intercomMappings' }) ?? [];

  const addIntercomMapping = (): void => {
    append({ id: uuidv4(), name: '', buttonId: '', systemId: '', systemName: '' });
  };

  const deleteIntercomMapping = (index: number): void => {
    setDeletedIntercomMappings((mappings) => [...mappings, intercomMappings[index]]);
    replace(intercomMappings.filter((_, mappingIndex) => mappingIndex !== index));
  };

  return {
    intercomMappings,
    deletedIntercomMappings,
    addIntercomMapping,
    deleteIntercomMapping,
  };
};

const useIntercomMapping = () => {
  const form = useFormContext<IntercomMappingsForm>();
  const { intercomMappings, deletedIntercomMappings, addIntercomMapping, deleteIntercomMapping } = useIntercomMappings();
  const { currentDevice, currentGatewayId, currentViewingMode } = useDeviceDetailsHook();
  const { mutateAsync: validateIntercomMappingButtonId } = useValidateIntercomMappingButtonId();
  const [mappingIndexToLink, setMappingIndexToLink] = useState<number>();
  const previousGatewayId = useRef(currentGatewayId);

  useEffect(() => {
    const hasGatewayChanged = previousGatewayId.current !== undefined && previousGatewayId.current !== currentGatewayId && currentGatewayId;

    if (hasGatewayChanged) {
      intercomMappings.forEach((intercomMapping, index) => {
        if (intercomMapping.systemId) {
          form.setValue(`intercomMappings.${index}.systemId`, undefined, { shouldDirty: true, shouldValidate: true });
          form.setValue(`intercomMappings.${index}.systemName`, undefined, { shouldDirty: true });
        }

        if (intercomMapping.buttonId.trim()) {
          void form.trigger(`intercomMappings.${index}.buttonId`);
        }
      });
    }

    previousGatewayId.current = currentGatewayId;
  }, [currentGatewayId, form, intercomMappings]);

  const handleOnSystemSelected = (system: ReadIntercomIpRoomUnitsSystemResponse): void => {
    if (mappingIndexToLink === undefined) {
      return;
    }

    form.setValue(`intercomMappings.${mappingIndexToLink}.systemId`, system.systemId, { shouldDirty: true, shouldValidate: true });
    form.setValue(`intercomMappings.${mappingIndexToLink}.systemName`, system.name, { shouldDirty: true });
  };

  const unlinkSystem = (index: number): void => {
    form.setValue(`intercomMappings.${index}.systemId`, undefined, { shouldDirty: true, shouldValidate: true });
    form.setValue(`intercomMappings.${index}.systemName`, undefined, { shouldDirty: true });
  };

  const validateButtonId = async (buttonId: string, index: number): Promise<ButtonIdValidationResult> => {
    const trimmedButtonId = buttonId.trim();
    const normalizedButtonId = trimmedButtonId.toLowerCase();
    if (!trimmedButtonId || !currentGatewayId) {
      return true;
    }

    const hasMatchingButtonId = intercomMappings.some(
      (intercomMapping, mappingIndex) => mappingIndex !== index && intercomMapping.buttonId.trim().toLowerCase() === normalizedButtonId,
    );
    if (hasMatchingButtonId) {
      return 'notUnique';
    }

    try {
      await validateIntercomMappingButtonId({
        intercomMappingId:
          currentViewingMode === 'editing'
            ? (deletedIntercomMappings.find((mapping) => mapping.buttonId.trim().toLowerCase() === normalizedButtonId)?.id ??
              intercomMappings[index]?.id)
            : undefined,
        gatewayId: currentGatewayId,
        buttonId: trimmedButtonId,
      });
      return true;
    } catch {
      return 'notUnique';
    }
  };

  return {
    currentDevice,
    currentViewingMode,
    intercomMappings,
    currentGatewayId,
    mappingIndexToLink,
    addIntercomMapping,
    deleteIntercomMapping,
    unlinkSystem,
    validateButtonId,
    form,
    selectSystem: (index: number): void => setMappingIndexToLink(index),
    handleOnClose: (): void => setMappingIndexToLink(undefined),
    handleOnSystemSelected,
  };
};

export default useIntercomMapping;
